import { useState } from "react";
import type { AiSuggestion, SuggestionFeedbackAction } from "../api/client.js";

export const LOW_CONFIDENCE_THRESHOLD = 0.7;

export const suggestionActions = {
  copied: (): SuggestionFeedbackAction => ({ type: "COPIED" }),
  editedAndCopied: (editedText: string): SuggestionFeedbackAction => ({
    editedText,
    type: "EDITED_AND_COPIED"
  }),
  escalated: (): SuggestionFeedbackAction => ({
    reason: "USER_ESCALATED_FROM_WORKBENCH",
    type: "ESCALATED"
  }),
  ratedDown: (): SuggestionFeedbackAction => ({
    rating: "DOWN",
    reason: "NOT_USEFUL",
    type: "RATED"
  }),
  ratedUp: (): SuggestionFeedbackAction => ({ rating: "UP", type: "RATED" })
};

export function AiPanel(props: {
  canAct: boolean;
  hasValidOrder: boolean;
  onAction: (suggestionId: string, action: SuggestionFeedbackAction) => Promise<void>;
  onUseInComposer?: (text: string) => void;
  suggestions: readonly AiSuggestion[];
}): React.JSX.Element {
  return (
    <section aria-label="AI 建议" className="inspector-ai">
      <h2>AI 建议</h2>
      <p className="inspector-helper">建议仅供辅助判断。发送前请核对事实、语气与联系策略。</p>
      {props.suggestions.length === 0 ? (
        props.hasValidOrder
          ? <p className="inspector-empty-note">暂无建议。收到新的文本消息后会自动进行安全分析。</p>
          : <p className="inspector-empty-note">请先录入完整有效的贷款订单；缺少订单事实时不会生成建议。</p>
      ) : null}
      {props.suggestions.map((suggestion) => (
        <SuggestionCard
          canAct={props.canAct && suggestion.isCurrent !== false}
          key={suggestion.id}
          onAction={props.onAction}
          {...(props.onUseInComposer === undefined ? {} : { onUseInComposer: props.onUseInComposer })}
          suggestion={suggestion}
        />
      ))}
    </section>
  );
}

function SuggestionCard(props: {
  canAct: boolean;
  onAction: (suggestionId: string, action: SuggestionFeedbackAction) => Promise<void>;
  onUseInComposer?: (text: string) => void;
  suggestion: AiSuggestion;
}): React.JSX.Element {
  const [editedText, setEditedText] = useState(props.suggestion.originalText);
  const [error, setError] = useState<string | null>(null);
  const isElevated = props.suggestion.requiresApproval ||
    props.suggestion.risks.length > 0 ||
    props.suggestion.confidence < LOW_CONFIDENCE_THRESHOLD;

  async function act(action: SuggestionFeedbackAction, copyText?: string): Promise<void> {
    setError(null);
    try {
      if (copyText !== undefined && typeof navigator !== "undefined") {
        await navigator.clipboard?.writeText(copyText);
      }
      await props.onAction(props.suggestion.id, action);
    } catch {
      setError("操作失败，请重试。");
    }
  }

  return (
    <article className="inspector-ai-card">
      <div className="inspector-card-heading"><h3>{props.suggestion.intent}</h3><span className="inspector-status">置信度 {Math.round(props.suggestion.confidence * 100)}%</span></div>
      {isElevated ? (
        <p aria-label="风险警示" role="alert">
          需要重点人工复核；
          风险 {props.suggestion.risks.join("、") || "低置信度"}
        </p>
      ) : null}
      {props.suggestion.isCurrent === false ? <p role="status">此建议已失效，仅保留作审计参考；请等待当前消息的新建议。</p> : null}
      <textarea
        aria-label="建议文本"
        onChange={(event) => { setEditedText(event.currentTarget.value); }}
        readOnly={!props.canAct}
        value={editedText}
      />
      <details>
        <summary>判断依据</summary>
        <ul>{props.suggestion.rationale.map((item) => <li key={item}>{item}</li>)}</ul>
        <p>模型 {props.suggestion.modelVersion} · 策略 {props.suggestion.policyVersion}</p>
      </details>
      {props.canAct ? (
        <div aria-label="建议操作" className="inspector-ai-actions">
          <button
            onClick={() => { void act(suggestionActions.copied(), props.suggestion.originalText); }}
            type="button"
          >复制原建议</button>
          <button
            onClick={() => { void act(suggestionActions.editedAndCopied(editedText), editedText); }}
            type="button"
          >编辑后复制</button>
          {props.onUseInComposer === undefined ? null : <button
            onClick={() => { props.onUseInComposer?.(editedText); }}
            type="button"
          >填入消息框</button>}
          <button onClick={() => { void act(suggestionActions.ratedUp()); }} type="button">
            有帮助
          </button>
          <button
            onClick={() => { void act(suggestionActions.ratedDown()); }}
            type="button"
          >没帮助</button>
          <button
            onClick={() => { void act(suggestionActions.escalated()); }}
            type="button"
          >转主管</button>
        </div>
      ) : null}
      {error === null ? null : <p role="alert">{error}</p>}
    </article>
  );
}
